<template>
  <div
    class="flex flex-grow items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8"
  >
    <div class="max-w-md w-full space-y-8">
      <div>
        <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
          Create your account
        </h2>
        <p class="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
          Or
          <router-link
            to="/login"
            class="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
          >
            sign in to your existing account
          </router-link>
        </p>
      </div>

      <form class="mt-8 space-y-6" @submit.prevent="handleRegister">
        <div class="space-y-4">
          <div>
            <label for="username" class="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >Username</label
            >
            <input
              id="username"
              v-model="form.username"
              name="username"
              type="text"
              required
              class="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Enter your username"
            />
          </div>

          <div>
            <label for="email" class="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >Email address</label
            >
            <input
              id="email"
              v-model="form.email"
              name="email"
              type="email"
              autocomplete="email"
              required
              class="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Enter your email"
            />
          </div>

          <div>
            <label for="password" class="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >Password</label
            >
            <input
              id="password"
              v-model="form.password"
              name="password"
              type="password"
              autocomplete="new-password"
              required
              class="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Enter your password"
            />
          </div>

          <div>
            <label
              for="confirmPassword"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >Confirm Password</label
            >
            <input
              id="confirmPassword"
              v-model="form.confirmPassword"
              name="confirmPassword"
              type="password"
              autocomplete="new-password"
              required
              class="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Confirm your password"
            />
          </div>
        </div>

        <div v-if="error" class="text-red-600 dark:text-red-400 text-sm text-center">
          {{ error }}
        </div>

        <div>
          <Button
            type="submit"
            :disabled="loading || !isFormValid"
            :label="loading ? 'Creating account...' : 'Create account'"
            severity="primary"
            :loading="loading"
            class="w-full"
          />
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "../../stores/auth";
import api from "../../services/api";
import Button from "primevue/button";

export default {
  name: "Register",
  components: {
    Button
  },
  setup() {
    const router = useRouter();
    const authStore = useAuthStore();

    const form = ref({
      username: "",
      email: "",
      password: "",
      confirmPassword: ""
    });
    const error = ref("");
    const loading = ref(false);

    const isFormValid = computed(() => {
      return (
        form.value.username &&
        form.value.email &&
        form.value.password &&
        form.value.confirmPassword &&
        form.value.password === form.value.confirmPassword
      );
    });

    const checkRegistrationStatus = async () => {
      try {
        const response = await api.get("/api/settings/registration/public");
        if (response.data.success) {
          const registrationEnabled = response.data.data.registrationEnabled;
          if (!registrationEnabled) {
            // Registration is disabled, redirect to login
            router.push("/login");
            return;
          }
        } else {
          // If the API doesn't return success, default to allowing registration
          // (since default is enabled)
          console.warn("Registration status check returned unsuccessful, defaulting to enabled");
        }
      } catch (err) {
        // If we can't check the status, default to allowing registration
        // (since default backend behavior is enabled)
        console.error("Could not check registration status:", err);
        // Don't redirect - allow registration to proceed (default is enabled)
      }
    };

    const handleRegister = async () => {
      if (form.value.password !== form.value.confirmPassword) {
        error.value = "Passwords do not match";
        return;
      }

      try {
        loading.value = true;
        error.value = "";

        const result = await authStore.register({
          username: form.value.username,
          email: form.value.email,
          password: form.value.password
        });

        if (result.success) {
          router.push("/");
        } else {
          error.value = result.message || "Registration failed";
        }
      } catch (err) {
        error.value = "An unexpected error occurred";
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      checkRegistrationStatus();
    });

    return {
      form,
      error,
      loading,
      isFormValid,
      handleRegister
    };
  }
};
</script>
